import React, { useState, useRef, useEffect } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";
import NavBar from "../../components/navbar";
import "./QrScanner.css";
import { Button, dialog } from "../../nq-component";
import { useNavigate } from "react-router-dom";
import {
  findObjectUseCase,
  upsertUseCase,
  getObjectUseCase,
} from "../../usecases/object";
import DialogExists from "./DialogExists";
import DialogSuccess from "./DialogSuccess";

// QR schedules use local wall-clock times (see formatDate in my-listings/utils).
// API values may include a trailing "Z", but the digits still represent local time.
const parseScheduleDate = (dateStr) => {
  if (!dateStr) return new Date(NaN);

  const match = dateStr.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:Z)?$/,
  );

  if (match) {
    const [, year, month, day, hour, minute, second, ms] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      ms ? Number(ms.padEnd(3, "0").slice(0, 3)) : 0,
    );
  }

  return new Date(dateStr);
};

const isWithinScheduleRange = (startDateStr, endDateStr) => {
  const start = parseScheduleDate(startDateStr);
  const end = parseScheduleDate(endDateStr);
  const now = new Date();

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return false;
  }

  return now >= start && now <= end;
};

const SCHEDULE_OUT_OF_RANGE_MESSAGE =
  "Your schedule is not within the scheduled time.";

const formatScheduleDateTime = (dateStr) => {
  const date = parseScheduleDate(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const ScheduleOutOfRangeDialog = ({ startDateStr, endDateStr, onOk }) => (
  <DialogExists message={SCHEDULE_OUT_OF_RANGE_MESSAGE} onOk={onOk}>
    <div className="mt-2">
      <p className="mb-1">
        <strong>Scheduled Start:</strong> {formatScheduleDateTime(startDateStr)}
      </p>
      <p className="mb-0">
        <strong>Scheduled End:</strong> {formatScheduleDateTime(endDateStr)}
      </p>
    </div>
  </DialogExists>
);

const QrScanner = () => {
  const [link, setLink] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());
  const navigate = useNavigate();
  const hasActiveScanRef = useRef(false);

  const startScanning = async () => {
    try {
      const devices = await codeReader.current.listVideoInputDevices();

      if (devices.length === 0) {
        throw new Error("No video input devices found.");
      }

      const selectedDeviceId = devices[0].deviceId;

      await codeReader.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        async (result, error) => {
          if (result) {
            if (hasActiveScanRef.current) {
              return;
            }

            const scannedLink = result.getText();
            hasActiveScanRef.current = true;
            setLink(scannedLink);

            try {
              await handleDetectedQRCode(scannedLink);
            } catch (err) {
              console.error("Error handling QR code:", err);
              hasActiveScanRef.current = false;
            }
          }
          if (error && error.name !== "NotFoundException") {
            console.error("Error scanning QR code:", error);
          }
        },
      );
    } catch (error) {
      console.error("Camera init failed:", error);
    }
  };

  useEffect(() => {
    startScanning();

    return () => {
      codeReader.current.reset();
    };
  }, []);

  const onClose = () => {
    dialog.close();
    hasActiveScanRef.current = false;
  };

  const releaseScan = () => {
    hasActiveScanRef.current = false;
  };

  const handleDetectedQRCode = async (scannedLink) => {
    if (!scannedLink) return;

    const [
      firstName,
      lastName,
      startDateStr,
      endDateStr,
      recruiter,
      recruiterFirstName,
      recruiterMiddleName,
      recruiterLastName,
      recruiterEmpId,
      positionApplied,
      window,
    ] = scannedLink.split("/");

    const fullName = `${firstName} ${lastName}`;

    const formattedStart = formatScheduleDateTime(startDateStr);
    const formattedEnd = formatScheduleDateTime(endDateStr);

    if (!isWithinScheduleRange(startDateStr, endDateStr)) {
      dialog.fire({
        html: (
          <ScheduleOutOfRangeDialog
            startDateStr={startDateStr}
            endDateStr={endDateStr}
            onOk={releaseScan}
          />
        ),
        footer: false,
      });
      return;
    }

    dialog.fire({
      html: (
        <div className="d-flex flex-column align-items-center justify-content-center p-3">
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <h3 style={{ marginLeft: "5px" }}>
                <i
                  className="bi bi-qr-code-scan"
                  style={{ color: "#ffb828" }}
                ></i>{" "}
                QR Information
              </h3>
              <i
                className="bi bi-x-lg mb-4"
                onClick={() => onClose()}
                style={{ cursor: "pointer", fontSize: "20px" }}
              ></i>
            </div>

            <div className="border p-3 rounded bg-white">
              <p>
                <strong>Name:</strong> {fullName}
              </p>
              <p>
                <strong>Start Date:</strong> {formattedStart}
              </p>
              <p>
                <strong>End Date:</strong> {formattedEnd}
              </p>
            </div>
          </div>
          <button
            className="btn shadow-sm"
            onClick={() => handleSendQueue(scannedLink)}
            style={{
              marginTop: "20px",
              backgroundColor: "#517fc1",
              color: "white",
            }}
          >
            Enter Applicant
          </button>
        </div>
      ),
      footer: false,
    });
  };

  async function handleSendQueue(link) {
    console.log("link", link);
    const [
      firstName,
      lastName,
      startDateStr,
      endDateStr,
      recruiter,
      recruiterFirstName,
      recruiterMiddleName,
      recruiterLastName,
      recruiterEmpId,
      positionApplied,
      windowSlot,
    ] = link.split("/");
    console.log("link", link);

    if (!isWithinScheduleRange(startDateStr, endDateStr)) {
      dialog.fire({
        html: (
          <ScheduleOutOfRangeDialog
            startDateStr={startDateStr}
            endDateStr={endDateStr}
            onOk={releaseScan}
          />
        ),
        footer: false,
      });
      return;
    }

    const formatLastName = (name) => {
      if (!name || typeof name !== "string") return "";

      return name
        .split(" ")
        .filter((part) => part.trim().length > 0)
        .map((part) => part[0]?.toUpperCase() + ".")
        .join(" ");
    };

    const lastNameFormatted = formatLastName(lastName);
    const applicantExists = await findObjectUseCase().execute("queue", {
      where: {
        name: `${firstName} ${lastNameFormatted}`,
        startDate: startDateStr,
        endDate: endDateStr,
        recruiter: { id: recruiter },
      },
    });

    console.log("PPPPPP", applicantExists);

    const object = {
      name: `${firstName} ${lastNameFormatted}`,
      startDate: startDateStr,
      endDate: endDateStr,
      recruiter: { id: recruiter },
      // employeeId: recruiter.id,
      window: windowSlot,
      recruiterFirstName: recruiterFirstName,
      recruiterMiddleName: recruiterMiddleName,
      recruiterLastName: recruiterLastName,
      status: "waiting",
      positionApplied: positionApplied,
    };

    const report = {
      recruiter: { id: recruiter },
      applicant: `${firstName} ${lastNameFormatted}`,
      status: "showedUp",
    };

    try {
      if (applicantExists?.length <= 0) {
        await upsertUseCase().execute("recruiters_report", report);
        const response = await upsertUseCase().execute("queue", object);
        if (response) {
          hasActiveScanRef.current = false;
          dialog.close();
          dialog.fire({
            html: (
              <>
                <DialogSuccess
                  message={"Successfully Scanned the QR!"}
                  onOk={releaseScan}
                />
              </>
            ),
            footer: false,
          });
          setLink(null);
        }
      } else {
        dialog.fire({
          html: (
            <>
              <DialogExists
                message={"Already Scanned the QR!"}
                onOk={releaseScan}
              />
            </>
          ),
          footer: false,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  return (
    <div>
      <NavBar />
      <div className="p-3 p-lg-4">
        <div className="d-flex  align-items-center mb-3">
          <h1 className="fw-bold text-capitalize mt-1 ms-2">QR Scanner</h1>
        </div>

        <div
          className="card pt-1 ps-2 pe-2 pb-5 bg-light"
          //   style={{ height: "600px" }}
        >
          <div style={{ marginTop: "60px" }} className="video-container">
            <div className="video-border">
              <div className="corner-top-right"></div>
              <div className="corner-bottom-left"></div>
              <div className="corner-top-left"></div>
              <div className="corner-bottom-right"></div>
              <video ref={videoRef} style={{ width: "100%" }} />
              <div className="scanning-line"></div>{" "}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrScanner;
